/**
 * Which released hours this person has waved away, remembered across reloads.
 *
 * Ruled 30 September. Dismissing a row used to last only as long as the page:
 * the × forgets it in memory, nothing is written down — deliberately, because
 * nothing is deleted server-side — so a refresh read the lapsed cart again and
 * the row came back. Within the hour it could come back several times, which
 * reads as the app arguing with her.
 *
 * ON THIS DEVICE, IN THIS BROWSER. That is the whole promise, and it is the
 * honest one: the cart row still exists, so the same person on their phone sees
 * the hour again. Anything stronger means either deleting a parent's cart line
 * on their behalf or inventing a server-side "dismissed" record, and neither is
 * worth it for a row that ages out in an hour by itself.
 *
 * Entries carry the moment they stop mattering, so the store prunes itself
 * rather than growing for the life of the browser.
 */

const KEY = 'coachtools.released.dismissed';

/** Comfortably past the 60 minutes after which a released hour stops being offered. */
const KEEP_MS = 2 * 60 * 60 * 1000;

type Store = Record<string, number>;

/**
 * Every read and write is wrapped, and every failure is silence.
 *
 * Storage throws rather than returning null in a private window and under some
 * enterprise policies, and this is a convenience — a parent who dismissed a row
 * an hour ago. A banner that renders is worth more than a dismissal that sticks,
 * so nothing here is allowed to take the screen down with it.
 */
function read(): Store {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};

    const now = Date.now();
    const kept: Store = {};
    for (const [key, until] of Object.entries(parsed as Store)) {
      if (typeof until === 'number' && until > now) kept[key] = until;
    }
    return kept;
  } catch {
    return {};
  }
}

/** The keys still worth hiding, oldest entries already dropped. */
export function loadDismissed(): string[] {
  return Object.keys(read());
}

/** Adds one key, keeping what is already there and pruning what has aged out. */
export function rememberDismissed(key: string): void {
  try {
    const store = read();
    store[key] = Date.now() + KEEP_MS;
    window.localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // The row stays dismissed for this page either way; only the memory is lost.
  }
}
